import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';

/**
 * One Spotlight chip — compact, single-line pill rendering
 * `[icon] [label] | [count]`. Toggles active state via the parent's
 * `onClick`. The subtitle text (e.g. "Owners likely to sell") is shown as
 * a custom dark tooltip from `react-tooltip` — same `variant="dark"`
 * pattern the rest of the platform uses (see CompanyResult.jsx) rather
 * than the browser-native `title` pop-up, so the chip stays consistent
 * with td-frontend's tooltip aesthetic.
 *
 * Visual style is anchored to `@action-blue` for the active state to stay
 * consistent with the rest of td-frontend's accent language.
 *
 * Used inside SpotlightsBar. Definition shape: see spotlightDefinitions.js.
 */
const SpotlightChip = observer(
  ({ definition, count = 0, active = false, disabled = false, onClick = () => {} }) => {
    const anchorId = `spotlight-chip-${definition.id}`;
    return (
      <>
        <button
          type="button"
          id={anchorId}
          className={`spotlight-chip ${active ? 'active' : ''}`}
          onClick={onClick}
          disabled={disabled}
          aria-pressed={active}
        >
          <img className="spotlight-chip-icon" src={definition.icon} alt="" aria-hidden="true" />
          <span className="spotlight-chip-label">{definition.label}</span>
          <span className="spotlight-chip-count">{count.toLocaleString('en-US')}</span>
        </button>
        <Tooltip
          anchorSelect={`#${anchorId}`}
          content={definition.subtitle}
          variant="dark"
          place="bottom-center"
          opacity={1}
          className="spotlight-chip-tooltip"
        />
      </>
    );
  }
);

SpotlightChip.propTypes = {
  definition: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    match: PropTypes.func.isRequired
  }).isRequired,
  count: PropTypes.number,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func
};

export default SpotlightChip;
